/* eslint-disable @typescript-eslint/naming-convention */
import { AxiosResponse, AxiosError } from "axios";
import request from "#/utils/request";
import InternalResponse from "#/types/internalResponse";
import { authHeaders } from "#/modules/auth/idp";

export type ServiceTokenRequestType = {
  params?: {
    service_token_status?: string;
  };
  token: string;
};

export type ServiceTokenType = {
  service_token_name: string;
  service_token_id: string;
  token?: string;
  description: string;
  service_token_status: "enable" | "disable";
  created_at: string;
  expires_at: string;
  division: string;
  policy_names: string[];
  policy_id_list: string[];
  allow_pattern_keywords?: string[];
  allow_pattern_regexes?: string[];
};

export type CreateServiceTokenParams = {
  token: string;
  data: {
    service_token_name: string;
    expires_at: string;
    service_token_status?: string;
    description?: string;
    timezone?: string;
    division?: string;
  };
};

// eslint-disable-next-line prefer-destructuring
const USE_MOCK = process.env.USE_MOCK;

function generateMockTokenId(): string {
  const hex = () => Math.random().toString(16).substring(2, 10);
  return `xgs_${hex()}${hex()}_${hex()}${hex()}${hex()}`;
}

export function createServiceToken(
  { token, data }: CreateServiceTokenParams,
): Promise<InternalResponse<ServiceTokenType>> {
  if (USE_MOCK) {
    const now = new Date().toISOString();
    const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString();
    return Promise.resolve({
      success: true,
      statusCode: 201,
      data: {
        service_token_name: data.service_token_name,
        service_token_id: generateMockTokenId(),
        token: generateMockTokenId(),
        description: data.description ?? "",
        service_token_status: "enable",
        created_at: now,
        expires_at: expires,
        division: data.division ?? "",
        policy_names: [],
        policy_id_list: [],
        allow_pattern_keywords: [],
        allow_pattern_regexes: [],
      },
    });
  }

  return request()({
    url: "/xecguard/v1/tokens",
    method: "POST",
    data,
    headers: authHeaders(token),
  }).then(
    (result: AxiosResponse<ServiceTokenType>): InternalResponse<ServiceTokenType> => ({
      statusCode: result.status,
      data: result.data,
      success: true,
    }),
  ).catch((error: AxiosError): InternalResponse => ({
    statusCode: error.code,
    msg: error.message,
    success: false,
  }));
}

export type PatchServiceTokenParams = {
  serviceTokenId: string;
  token: string;
  data: {
    policy_id_list?: string[];
    allow_pattern_keywords?: string[];
    allow_pattern_regexes?: string[];
    service_token_name?: string;
    division?: string;
    description?: string;
    service_token_status?: string;
  };
};

export function patchServiceToken(
  { serviceTokenId, token, data }: PatchServiceTokenParams,
): Promise<InternalResponse<ServiceTokenType>> {
  return request()({
    url: `/xecguard/v1/tokens/${serviceTokenId}`,
    method: "PATCH",
    data,
    headers: authHeaders(token),
  }).then(
    (result: AxiosResponse<ServiceTokenType>): InternalResponse<ServiceTokenType> => ({
      statusCode: result.status,
      data: result.data,
      success: true,
    }),
  ).catch((error: AxiosError): InternalResponse => ({
    statusCode: error.code,
    msg: error.message,
    success: false,
  }));
}

export type DeleteServiceTokenParams = {
  serviceTokenId: string;
  token: string;
};

export function deleteServiceToken(
  { serviceTokenId, token }: DeleteServiceTokenParams,
): Promise<InternalResponse> {
  return request()({
    url: `/xecguard/v1/tokens/${serviceTokenId}`,
    method: "DELETE",
    headers: authHeaders(token),
  }).then(
    (result: AxiosResponse): InternalResponse => ({
      statusCode: result.status,
      success: true,
    }),
  ).catch((error: AxiosError): InternalResponse => ({
    statusCode: error.code,
    msg: error.message,
    success: false,
  }));
}

export type RegenerateServiceTokenParams = {
  serviceTokenId: string;
  token: string;
};

export type RegenerateServiceTokenResponse = ServiceTokenType & {
  token: string;
};

export function regenerateServiceToken(
  { serviceTokenId, token }: RegenerateServiceTokenParams,
): Promise<InternalResponse<RegenerateServiceTokenResponse>> {
  return request()({
    url: `/xecguard/v1/tokens/${serviceTokenId}/regenerate`,
    method: "POST",
    headers: authHeaders(token),
  }).then(
    (result: AxiosResponse<RegenerateServiceTokenResponse>):
    InternalResponse<RegenerateServiceTokenResponse> => ({
      statusCode: result.status,
      data: result.data,
      success: true,
    }),
  ).catch((error: AxiosError): InternalResponse => ({
    statusCode: error.code,
    msg: error.message,
    success: false,
  }));
}

export function getServiceToken({
  serviceTokenId,
  token,
}: {
  serviceTokenId: string;
  token: string;
}): Promise<InternalResponse<ServiceTokenType>> {
  return request()({
    url: `/xecguard/v1/tokens/${serviceTokenId}`,
    method: "GET",
    headers: authHeaders(token),
  }).then(
    (result: AxiosResponse<ServiceTokenType>): InternalResponse<ServiceTokenType> => ({
      statusCode: result.status,
      data: result.data,
      success: true,
    }),
  ).catch((error: AxiosError): InternalResponse => ({
    statusCode: error.code,
    msg: error.message,
    success: false,
  }));
}

export default function getServiceTokens({ params, token }: ServiceTokenRequestType): Promise<InternalResponse> {
  return request()({
    url: "/xecguard/v1/tokens",
    method: "GET",
    params: params && Object.keys(params).length > 0 ? params : undefined,
    headers: authHeaders(token),
  }).then(
    (result: AxiosResponse<ServiceTokenType>): InternalResponse<ServiceTokenType> => ({
      statusCode: result.status,
      data: result.data,
      success: true,
    }),
  ).catch((error: AxiosError): InternalResponse => ({
    statusCode: error.code,
    msg: error.message,
    success: false,
  }));
}
